import { ArrowUpRight, ThermometerSun, Wind } from "lucide-react";
import { AnimatedNumber } from "@/components/aion/AnimatedNumber";

const isOn = (device) => String(device?.state?.power || device?.reported_state?.power || "").toLowerCase() === "on";

const firstNumber = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

const average = (items) => {
  const valid = items.filter((item) => Number.isFinite(item));
  if (!valid.length) return null;
  return valid.reduce((sum, item) => sum + item, 0) / valid.length;
};

const climateTypes = new Set(["climate", "hvac", "thermostat", "heater", "ac"]);

export const AionRoomStateCard = ({ room, index }) => {
  const devices = room?.devices || [];
  const activeDevices = devices.filter(isOn);
  const lightsOn = devices.filter((device) => (device.type || device.device_type) === "light" && isOn(device)).length;
  const offlineDevices = devices.filter((device) => device.connection_state === "offline").length;
  const blindPositions = devices
    .filter((device) => (device.type || device.device_type) === "blind")
    .map((device) => firstNumber(device.state?.position, device.reported_state?.position, device.desired_state?.position));
  const blindPosition = average(blindPositions);
  const hvacDevices = devices.filter((device) => climateTypes.has(String(device.type || device.device_type || "").toLowerCase()));
  const hvacMode = hvacDevices.find((device) => device.state?.mode)?.state?.mode || "idle";
  const hvacTemperature = average(hvacDevices.map((device) => firstNumber(device.state?.current_temperature, device.state?.temperature, device.state?.target_temperature)));
  const batteryLevel = average(devices.map((device) => firstNumber(device.state?.battery, device.battery)));
  const signalLevel = average(devices.map((device) => firstNumber(device.state?.signal_quality, device.signal_quality)));
  const sensorTemperature = average(devices.map((device) => firstNumber(device.state?.temperature, device.state?.current_temperature)));
  const sensorHumidity = average(devices.map((device) => firstNumber(device.state?.humidity)));
  const tone = offlineDevices ? "warning" : lightsOn ? "active" : "calm";

  return (
    <section
      className={`aion-room-card aion-room-card-${tone}`}
      data-testid={`room-state-card-${index}`}
      data-room-tone={tone}
    >
      <div className="aion-room-card-header" data-testid={`room-state-card-header-${index}`}>
        <div>
          <p className="aion-section-eyebrow" data-testid={`room-state-card-eyebrow-${index}`}>ROOM</p>
          <h3 className="aion-room-card-title" data-testid={`room-state-card-title-${index}`}>{room.name}</h3>
          <p className="aion-room-card-copy" data-testid={`room-state-card-copy-${index}`}>
            {offlineDevices ? `${offlineDevices} Geräte brauchen Aufmerksamkeit.` : activeDevices.length ? `${activeDevices.length} Geräte sind gerade aktiv.` : "Aktuell ist der Raum ruhig."}
          </p>
        </div>
        <span className="aion-room-card-forward" aria-hidden="true" data-testid={`room-state-card-forward-${index}`}>
          <ArrowUpRight size={18} />
        </span>
      </div>

      <div className="aion-room-card-scene" data-testid={`room-state-card-scene-${index}`}>
        <div className="aion-room-lights" data-testid={`room-light-visual-${index}`} data-lights-on={lightsOn > 0}>
          <span className="aion-room-lights-glow" aria-hidden="true" />
          <div className="aion-room-light-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <small>Licht</small>
          <strong>
            <AnimatedNumber value={lightsOn} dataTestId={`room-lights-on-count-${index}`} /> an
          </strong>
        </div>

        <div className="aion-room-blinds" data-testid={`room-blinds-visual-${index}`}>
          <div className="aion-room-blinds-track" aria-hidden="true">
            <span className="aion-room-blinds-fill" style={{ "--aion-blinds-level": `${Math.max(0, Math.min(100, blindPosition ?? 0))}%` }} />
          </div>
          <small>Blinds</small>
          <strong>
            <AnimatedNumber value={blindPosition} decimals={0} suffix="%" fallback="—" dataTestId={`room-blinds-position-${index}`} />
          </strong>
        </div>

        <div className="aion-room-hvac" data-testid={`room-hvac-visual-${index}`} data-hvac-mode={hvacMode}>
          <div className="aion-room-hvac-icon" aria-hidden="true">
            {String(hvacMode).toLowerCase() === "heat" ? <ThermometerSun size={18} /> : <Wind size={18} />}
          </div>
          <small>HVAC</small>
          <strong>
            {hvacTemperature !== null ? (
              <AnimatedNumber value={hvacTemperature} decimals={1} suffix="°C" fallback="—" dataTestId={`room-hvac-temperature-${index}`} />
            ) : "—"}
          </strong>
        </div>
      </div>

      <div className="aion-room-card-metrics" data-testid={`room-state-card-metrics-${index}`}>
        <div className="aion-room-mini-metric" data-testid={`room-devices-count-${index}`}>
          <span>Geräte</span>
          <strong><AnimatedNumber value={devices.length} dataTestId={`room-devices-count-value-${index}`} /></strong>
        </div>
        <div className="aion-room-mini-metric" data-testid={`room-active-count-${index}`}>
          <span>Aktiv</span>
          <strong><AnimatedNumber value={activeDevices.length} dataTestId={`room-active-count-value-${index}`} /></strong>
        </div>
        <div className="aion-room-mini-metric" data-testid={`room-offline-count-${index}`}>
          <span>Offline</span>
          <strong><AnimatedNumber value={offlineDevices} dataTestId={`room-offline-count-value-${index}`} /></strong>
        </div>
      </div>

      <div className="aion-room-sensor-strip" data-testid={`room-sensor-strip-${index}`}>
        <div className="aion-room-sensor-pill" data-testid={`room-battery-pill-${index}`}>
          <span>Batterie</span>
          <strong><AnimatedNumber value={batteryLevel} decimals={0} suffix="%" fallback="—" dataTestId={`room-battery-value-${index}`} /></strong>
        </div>
        <div className="aion-room-sensor-pill" data-testid={`room-signal-pill-${index}`}>
          <span>Signal</span>
          <strong><AnimatedNumber value={signalLevel} decimals={0} suffix="%" fallback="—" dataTestId={`room-signal-value-${index}`} /></strong>
        </div>
        <div className="aion-room-sensor-pill" data-testid={`room-temperature-pill-${index}`}>
          <span>Temp.</span>
          <strong><AnimatedNumber value={sensorTemperature} decimals={1} suffix="°C" fallback="—" dataTestId={`room-temperature-value-${index}`} /></strong>
        </div>
        <div className="aion-room-sensor-pill" data-testid={`room-humidity-pill-${index}`}>
          <span>Feuchte</span>
          <strong><AnimatedNumber value={sensorHumidity} decimals={0} suffix="%" fallback="—" dataTestId={`room-humidity-value-${index}`} /></strong>
        </div>
      </div>
    </section>
  );
};